<template>
  <div class="home_container" :class="{ home_rate_limited: rateLimited }">
    <div v-if="rateLimited" class="rate_limit_empty">
      <img class="rate_limit_img" :src="rateLimitImg" />
      <p class="rate_limit_text">{{ $t('当前使用人数过多,请稍后再试') }}</p>
      <button class="rate_limit_btn" @click="handleRetry">{{ $t('重试') }}</button>
    </div>
    <template v-else>
      <HomeHeader :collegeDetail="collegeDetail" :collegeList="collegeList" @setIdHandle="setCollegeIdCache" />
      <PullToRefresh v-model="refreshing" @refresh="onRefresh('0')">
        <List v-model:loading="loading" :finished="finished" @load="onLoad">
          <template v-for="(item, index) in modules" :key="index">
            <Banner v-if="item.code === 'app-banner'" :data="item" />
            <Menu v-if="item.code === 'app-nav'" :data="item" :badgeNum="badgeNum" />
            <Announcement v-if="item.code === 'app-notice'" :data="item" />
            <TodoList v-if="item.code === 'app-learn-task'" :data="item" :badgeNum="badgeNum" />
            <LiveStream v-if="item.code === 'app-live-stream'" :data="item" />
            <Course v-if="item.code === 'app-class'" :data="item" />
            <Course v-if="item.code === 'app-category-class'" :data="item" />
            <Broadcast v-if="item.code === 'app-info'" :data="item" />
            <BroadcastAndCourse v-if="item.code === 'app-info-class'" :data="item" />
            <Popularize v-if="item.code === 'app-multi-image'" :data="item" />
            <div v-if="item.code === 'app-community'" class="community_wrapper">
              <CardTitle :title="item.title" class="community_title" skipPage="/community" />
              <ArticleCard :articleData="getCommunityArticleData(item)" :active="0" @showPreview="showPreview" />
            </div>
            <Instructor v-if="item.code === 'app-lecturer'" :data="item" />
          </template>
          <template #loading>
            <Loading size="20" />
          </template>
        </List>
      </PullToRefresh>
      <Advertise v-model="advertiseShow" :data="advertiseList" />
      <BackTop bottom="100px" />
      <ImgPreview
        :imageList="imageList"
        :previewIndex="previewIndex"
        :previewShow="previewShow"
        @closePreview="handleClose"
      ></ImgPreview>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onActivated, onMounted, getCurrentInstance, ComponentInternalInstance, reactive } from 'vue'
import { List, Loading } from 'vant'
import { storeToRefs } from 'pinia'
import 'swiper/css'
import 'swiper/css/autoplay'
import HomeHeader from './HomeHeader.vue'
import Banner from './Banner.vue'
import Menu from './Menu.vue'
import Course from './Course.vue'
import Announcement from './Announcement.vue'
import TodoList from './TodoList.vue'
import LiveStream from './LiveStream.vue'
import Broadcast from './Broadcast.vue'
import BroadcastAndCourse from './BroadcastAndCourse.vue'
import Popularize from './Popularize.vue'
import Instructor from './Instructor.vue'
import { useThemeColorWithAlpha } from '@/utils/theme'
import { postCollegeList, postHomeConfig } from '@/api/requests/HomeRequests'
import { CollegeDetail, LmsDecorateModule } from '@/api/Models/HomeConfig'
import CardTitle from './CardTitle.vue'
import { Record as CommunityRecord } from '@/api/Models/CommunityBoardContentList'
import ArticleCard from '@/views/community/components/ArticleCard.vue'
import APIRequest from '@/api'
import { useUsersStore } from '@/stores/users'
import { useAuthStore } from '@/stores/login'
import { useSystemStore } from '@/stores/system'
import { College } from '@/api/Models/CollegeList'
import Advertise from '@/views/home/Advertise.vue'
import { Record as AdvertiseRecord } from '@/api/Models/HomeAdvertiseList'
import BackTop from '@/components/BackTop.vue'
import { Attachment } from '@/api/Models/Attachment'
import ImgPreview from '@/components/ImgPreview.vue'
import PullToRefresh from '@/components/PullToRefresh.vue'

const rateLimited = ref(false)
const rateLimitImg = require('@/assets/images/empty/error.png')

const badgeNum = ref(0)
const getNum = async () => {
  const { data, code } = await APIRequest.MyLearning.getMyLearningTask({
    taskStatus: 100,
    size: 5,
    current: 1
  })
  if (code === 1) {
    badgeNum.value = data.total
  }
}
onActivated(() => {
  getNum()
})
onMounted(() => {
  getNum()
})

const imageList = ref<string[]>([] as string[])
const previewShow = ref(false)
const previewIndex = ref(0)
const showPreview = (data: { attachments: Attachment[]; index: number }) => {
  imageList.value = data?.attachments.map((item) => item.previewUrl || '')
  previewShow.value = true
  previewIndex.value = data.index
}
const handleClose = () => {
  previewShow.value = false
}

const advertiseList = ref<AdvertiseRecord[]>([])
const advertiseShow = ref(false)

const getAdvertise = async () => {
  // 优化：广告一天内只弹一次
  const now = new Date()
  const today = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`
  const lastAdvertiseDate = localStorage.getItem('lastAdvertiseDate')

  if (lastAdvertiseDate === today) {
    localStorage.removeItem('fromLogin')
    return
  }

  console.log('开始调用广告接口')
  const { code, data } = await APIRequest.Home.getHomeAdvertiseList({
    location: '0',
    current: 1,
    size: 4
  })

  console.log('广告接口返回:', { code, data })

  if (code === 1) {
    // 修改过滤逻辑，去除空格并检查URL有效性
    const records = (data.records || []).filter((e) => {
      const trimmedUrl = e.appImgUrl?.trim()
      return trimmedUrl && trimmedUrl.length > 0
    })

    console.log('过滤后的records:', records, 'length:', records.length)
    console.log('第一条数据的appImgUrl:', records[0]?.appImgUrl)

    if (records.length > 0) {
      // 清理URL中的空格
      const cleanedRecords = records.map((record) => ({
        ...record,
        appImgUrl: record.appImgUrl?.trim()
      }))

      // 先设置数据
      advertiseList.value = cleanedRecords
      console.log('广告数据已设置:', advertiseList.value)

      // 使用延迟确保页面完全加载后再显示弹窗
      advertiseShow.value = true
      // 记录今日已展示
      localStorage.setItem('lastAdvertiseDate', today)
    }
    localStorage.removeItem('fromLogin')
  } else {
    console.log('接口调用失败，code:', code)
    // 接口失败时也重置状态
    advertiseList.value = []
    advertiseShow.value = false
  }
}

const loading = ref(false)
const finished = ref(true)
const refreshing = ref(false)
let current = 2
let feedCommunitySort = 0
let feedCommunityBoardIds: number[] = []
let feedCommunityModuleId = ''
const communityDataMap = reactive<Record<string, CommunityRecord[]>>({})

const getCommunityArticleData = (item: LmsDecorateModule): CommunityRecord[] => {
  const list = communityDataMap[item.id] || []
  if (item.isFeedStream !== 1) {
    const maxCount = Number(item.dataMaxCount) || 0
    return maxCount > 0 ? list.slice(0, maxCount) : list
  }
  return list
}

const resetCommunityState = () => {
  current = 2
  feedCommunitySort = 0
  feedCommunityBoardIds = []
  feedCommunityModuleId = ''
  Object.keys(communityDataMap).forEach((key) => {
    delete communityDataMap[key]
  })
}

const onLoad = async () => {
  if (rateLimited.value) return
  if (!feedCommunityModuleId) {
    loading.value = false
    finished.value = true
    return
  }
  if (refreshing.value) {
    communityDataMap[feedCommunityModuleId] = []
    refreshing.value = false
  }
  const { code, data } = await APIRequest.Community.getCommunityBoardContentList({
    current,
    size: 12,
    sortType: (feedCommunitySort + 1).toString(),
    boardIds: feedCommunityBoardIds
  })
  loading.value = false
  if (code === 1) {
    console.log(data)
    if (!data.records || data.records.length === 0) {
      finished.value = true
    }
    current++
    if (!communityDataMap[feedCommunityModuleId]) {
      communityDataMap[feedCommunityModuleId] = []
    }
    communityDataMap[feedCommunityModuleId].push(...data.records)
  } else {
    finished.value = true
  }
}

const { proxy } = getCurrentInstance() as ComponentInternalInstance
proxy?.$bus.on('thumbup', ({ id, likeCount, isLike }: { id: string; likeCount: number; isLike: boolean }) => {
  Object.values(communityDataMap).some((list) => {
    const cur = list.find((item) => item.id === id)
    if (!cur) return false
    cur.isLike = isLike
    cur.likeCount = Number(likeCount)
    return true
  })
})

const setRateLimited = (value: boolean) => {
  rateLimited.value = value
  if (!value) return
  loading.value = false
  finished.value = true
  refreshing.value = false
  modules.value = []
  resetCommunityState()
  advertiseShow.value = false
  advertiseList.value = []
}

proxy?.$bus.on('home_rate_limited', () => {
  setRateLimited(true)
})

const usersStore = useUsersStore()
const { collegeId } = storeToRefs(usersStore)
const collegeIdCache = ref(collegeId.value)
const setCollegeIdCache = (id: string) => {
  collegeIdCache.value = id
}
const { setCollegeId } = usersStore
const onRefresh = (loadingType?: '0' | '1') => {
  if (rateLimited.value) return
  current = 2
  init(loadingType)
}

const collegeDetail = ref({} as CollegeDetail)
const modules = ref([] as LmsDecorateModule[])

const init = async (type?: '0' | '1') => {
  if (rateLimited.value) return
  finished.value = true
  resetCommunityState()
  const res = await postHomeConfig(
    {
      clientType: 3,
      collegeId: collegeIdCache.value,
      readCache: 0,
      switchCollege: 1,
      templateId: ''
    },
    type
  )
  // console.log(res)
  const { code, data } = res
  refreshing.value = false
  if (code === 429) {
    setRateLimited(true)
    return
  }
  if (code === 1) {
    setCollegeId(collegeIdCache.value)
    collegeDetail.value = data.collegeDetail
    modules.value = data.lmsDecorateModules
    for (let i = 0; i < data.lmsDecorateModules.length; i++) {
      const item = data.lmsDecorateModules[i]
      if (item.code === 'app-community') {
        const lmsDecorateModuleCategories = item.lmsDecorateModuleCategories?.[0]
        const json = lmsDecorateModuleCategories?.datas?.[0]?.json
        try {
          const parsed = json ? (JSON.parse(json) as CommunityRecord[]) : []
          communityDataMap[item.id] = parsed
        } catch (error) {
          console.log(error)
          communityDataMap[item.id] = []
        }
        if (item.isFeedStream === 1 && !feedCommunityModuleId) {
          feedCommunityModuleId = item.id
          feedCommunitySort = lmsDecorateModuleCategories?.dataShowRule || 0
          feedCommunityBoardIds = (lmsDecorateModuleCategories?.dataJumpIds || [])
            .map((id) => parseInt(String(id).trim(), 10))
            .filter((id) => Number.isFinite(id))
        }
      }
    }
    if (feedCommunityModuleId) {
      finished.value = false
    }
  }
}

const collegeList = ref<College[]>([])
const authStore = useAuthStore()
const { userId } = storeToRefs(authStore)

const bootstrapHome = async (loadingType?: '0' | '1') => {
  if (rateLimited.value) return
  collegeIdCache.value = collegeId.value

  const shouldCallAdvertise = localStorage.getItem('fromLogin')
  if (shouldCallAdvertise && shouldCallAdvertise === 'true') {
    getAdvertise()
  }

  const { code, data } = await postCollegeList()
  if (code === 429) {
    setRateLimited(true)
    return
  }
  if (code === 1) {
    if (collegeId.value === '-1') {
      collegeIdCache.value = data.colleges[0]?.id
    }
    collegeList.value = data.colleges
    onRefresh(loadingType)
  }
}

const handleRetry = async () => {
  setRateLimited(false)
  proxy?.$bus.emit('home_rate_limit_retry')
  await bootstrapHome('1')
}

watch(
  userId,
  async (newUserId, oldUserId) => {
    console.log(newUserId, oldUserId, userId, '============')
    await bootstrapHome()
  },
  {
    immediate: true
  }
)

const systemStore = useSystemStore()
const { lang } = storeToRefs(systemStore)
watch([collegeIdCache, lang], () => {
  if (rateLimited.value) return
  onRefresh()
})

const themeColorWithAlpha = useThemeColorWithAlpha(0.6)
</script>
<script lang="ts">
export default {
  name: 'tab'
}
</script>
<style lang="less" scoped>
@import url('@/styles/variables.less');

.home_container {
  padding-top: 44px;

  .text {
    color: @textPrimary;
  }

  .text2 {
    color: v-bind(themeColorWithAlpha);
  }
  .community_wrapper {
    margin: 12px 16px;
    border-radius: 8px;
    background-color: @contentBackground;
    .community_title {
      padding-left: 16px;
      padding-right: 16px;
    }
  }
}

.home_rate_limited {
  padding-top: 0;
}

.rate_limit_empty {
  height: calc(100vh - 50px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f4f5f7;
}

.rate_limit_img {
  width: 178px;
  height: 178px;
}

.rate_limit_text {
  margin-top: 8px;
  font-size: 14px;
  color: #999999;
  line-height: 20px;
  text-align: center;
}

.rate_limit_btn {
  margin-top: 16px;
  padding: 4px 18px;
  border-radius: 16px;
  border: 1px solid #c9c9c9;
  background: #ffffff;
  color: #1a1a1a;
  font-size: 14px;
}
</style>
